/**
 * @file LabDataPage.js
 * @module src/js/pages/memberZone/LabDataPage
 * @description
 * **Page component to display lab data uploaded by EDALab members.**
 * - Fetches lab data details from the `/api/lab-data` endpoint.
 * - Displays lab data date, title, and uploader in a paginated table.
 * - Allows members to upload, edit, or delete their own lab data.
 * - Includes a search bar to filter lab data by date, title, or uploader.
 * - Redirects to the login page if the user is not authenticated.
 *
 * @version
 * <p>
 *  2025-11-26: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-11-30: The "NEW" image added for news within 90 days. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Add `ExternalLink` icon to the EDA Lab Handbook link. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useCheckAuthAndFetch hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useDeleteEntry. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useUser } from "../../contexts/UserProvider";
import { useTheme } from "../../contexts/ThemeProvider";
import useCheckAuthAndFetch from "../../hooks/useCheckAuthAndFetch";
import useDeleteEntry from "../../hooks/useDeleteEntry";
import NumberPagination from "../../components/NumberPagination";
import PagedTable from "../../components/PagedTable";
import UploadForm from "../../components/UploadForm";
import SearchBar from "../../components/SearchBar";
import SpanARow from "../../components/SpanARow";
import { TextWithBr, NewTab, DownloadLink } from "../../components/UtilityComponents";
import newGif from '../../../images/new.gif';

export default function LabDataPage() {
    const { pathname } = useLocation();
    const { uid } = useUser();
    const { theme } = useTheme();
    const checkAuthAndFetch = useCheckAuthAndFetch();
    const [labData, setLabData] = useState([]);
    const [filteredLabData, setFilteredLabData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentFirstPage, setCurrentFirstPage] = useState(1);
    const [updatedID, setUpdatedID] = useState(-1);
    const [deleting, setDeleting] = useState(false);
    const [loading, setLoading] = useState(true);
    const headerRef = useRef(null);
    const deleteEntry = useDeleteEntry({ setData: setLabData, setDeleting });

    const numPageButtons = 10;
    const numItemsPerPage = 10;

    const labDataOnCurrentPage = useMemo(() => {
        if (!filteredLabData || filteredLabData.length === 0) return [];
        const start = (currentPage - 1) * numItemsPerPage;
        const end = currentPage * numItemsPerPage;
        return filteredLabData.slice(start, end);
    }, [filteredLabData, currentPage]);

    useEffect(() => {
        async function checkAuthAndFetchData() {
            const data = await checkAuthAndFetch({ request: "/api/lab-data" });
            setLabData(data || []);
            setLoading(false);
        }
        checkAuthAndFetchData();
    }, [checkAuthAndFetch]);

    const pagination = (
        <NumberPagination
            length={filteredLabData.length}
            numPageButtons={numPageButtons}
            numItemsPerPage={numItemsPerPage}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            currentFirstPage={currentFirstPage}
            setCurrentFirstPage={setCurrentFirstPage}
            headerRef={headerRef}
        />
    )

    // The reason for using useMemo here is to prevent unnecessary re-renders
    // You can find more details in the News.js component
    const filterKeys = useMemo(() => ['date', 'title', 'uploader'], []);
    const searchBar = (
        <SearchBar
            data={labData}
            setFilteredData={setFilteredLabData}
            setCurrentPage={setCurrentPage}
            setCurrentFirstPage={setCurrentFirstPage}
            filterKeys={filterKeys}
        />
    );

    const table = (
        <div className="table-wrapper">
            <table>
                <thead>
                    <tr>
                        <th className='hori-vert-center' width='15%'>Date</th>
                        <th className='hori-vert-center'>Title</th>
                        <th className='hori-vert-center' width='20%'>Uploader</th>
                    </tr>
                </thead>
                <tbody>
                    <UploadForm
                        request="/api/lab-data"
                        acceptList={[null]}
                        setData={setLabData}
                        text="📑 Upload lab data"
                        from={pathname}
                    />
                    {filteredLabData.length === 0 && (
                        <SpanARow
                            colSpan={3}
                            text="No lab data found"
                            loading={loading}
                        />
                    )}
                    {labDataOnCurrentPage.map((item, i) => (
                        item.other_ID !== updatedID ? (
                            <tr key={i}>
                                <td className='hori-vert-center'>
                                    {item.date}<br />
                                    {new Date(item.date) >= new Date(Date.now() - 90 * 24 * 60 * 60 * 1000) && (
                                        <img src={newGif} alt='New' style={{ marginLeft: '5px' }} />
                                    )}
                                </td>
                                <td style={{textAlign: 'left', verticalAlign: 'middle'}}>
                                    <DownloadLink file={item.file}>
                                        {item.title}
                                    </DownloadLink>
                                </td>
                                <td className='hori-vert-center'>
                                    {item.present_ID === uid ? (
                                        <>
                                            <button
                                                onClick={() => setUpdatedID(item.other_ID)}
                                                disabled={deleting}
                                                style={{ marginBottom: '15px' }}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => deleteEntry({
                                                    request: "/api/lab-data",
                                                    id: item.other_ID,
                                                    from: pathname,
                                                })}
                                                disabled={deleting}
                                                style={{
                                                    color: theme === 'dark' ? 'lightcoral' : 'red',
                                                    outline: `2px solid ${theme === 'dark' ? 'lightcoral' : 'rgba(255, 0, 0, 0.1)'}`,
                                                    outlineOffset: '-2px',
                                                }}
                                            >
                                                Delete
                                            </button>
                                        </>
                                    ) : <TextWithBr text={item.uploader} />}
                                </td>
                            </tr>
                        ) : (
                            <UploadForm
                                key={i}
                                request="/api/lab-data"
                                acceptList={[null]}
                                setData={setLabData}
                                item={item}
                                updatedID={updatedID}
                                setUpdatedID={setUpdatedID}
                                from={pathname}
                            />
                        )
                    ))}
                </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '35px' }}>
                <NewTab
                    href='https://www.notion.so/EDA-Lab-22b4346bf34e80d3a28afd82b0fede95'
                    requireAuth={true}
                    className='button'
                    iconLocation="inside"
                >EDA Lab 實驗室手冊</NewTab>
            </div>
        </div>
    );

    return (
        <PagedTable
            header="Lab Data"
            headerRef={headerRef}
            numberPagination={filteredLabData.length > numItemsPerPage ? pagination : null}
            table={table}
            searchBar={searchBar}
        />
    )
}
